import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ResourceClient } from '../../../../api/resource';
import { logError } from '../../../../utils/logger';
import { ConfirmationModal } from '../../../shared/components/feedback';
import DialogShell from '../../../tasks/components/DialogShell';

export interface CrudColumn<T> {
  label: string;
  align?: 'left' | 'right' | 'center';
  render: (row: T) => ReactNode;
}

/**
 * A small list + add/edit/delete for the payroll setup tables whose forms need more than the
 * generic ResourceManager offers (optional numbers stay null instead of becoming 0, and fields
 * that depend on each other).
 */
export default function CrudTable<T extends { id: number }, D>({
  entityName,
  client,
  columns,
  canEdit,
  emptyDraft,
  toDraft,
  toPayload,
  validate,
  renderForm,
  listParams,
  sort,
}: {
  entityName: string;
  client: ResourceClient<T>;
  columns: CrudColumn<T>[];
  canEdit: boolean;
  emptyDraft: () => D;
  toDraft: (row: T) => D;
  toPayload: (draft: D) => Record<string, unknown>;
  validate?: (draft: D) => string | null;
  renderForm: (draft: D, set: (patch: Partial<D>) => void) => ReactNode;
  listParams?: Record<string, unknown>;
  sort?: (a: T, b: T) => number;
}) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ row: T | null; draft: D } | null>(null);
  const [deleting, setDeleting] = useState<T | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    client
      .list(listParams)
      .then((r) => setRows(sort ? [...r].sort(sort) : r))
      .catch((e) => setError(logError({ message: `Could not load ${entityName.toLowerCase()}s`, error: e, code: 'PAYROLL_SETUP_LIST_FAILED' })))
      .finally(() => setLoading(false));
    // listParams is compared by value so a new object each render does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, entityName, JSON.stringify(listParams)]);

  useEffect(load, [load]);

  const save = async () => {
    if (!editing) return;
    const problem = validate?.(editing.draft);
    if (problem) {
      toast.error(problem);
      return;
    }
    setBusy(true);
    try {
      const payload = toPayload(editing.draft);
      if (editing.row) await client.update(editing.row.id, payload);
      else await client.create(payload);
      toast.success(`${entityName} saved.`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(logError({ message: `Could not save the ${entityName.toLowerCase()}`, error: e, code: 'PAYROLL_SETUP_SAVE_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await client.remove(deleting.id);
      toast.success(`${entityName} deleted.`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(logError({ message: `Could not delete the ${entityName.toLowerCase()}`, error: e, code: 'PAYROLL_SETUP_DELETE_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  const set = (patch: Partial<D>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e));

  return (
    <Stack gap={2}>
      {canEdit && (
        <Stack direction='row' justifyContent='flex-end'>
          <Button variant='contained' startIcon={<Icon icon='mdi:plus' />} onClick={() => setEditing({ row: null, draft: emptyDraft() })}>
            Add {entityName.toLowerCase()}
          </Button>
        </Stack>
      )}
      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}
      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.label} align={c.align}>
                  {c.label}
                </TableCell>
              ))}
              {canEdit && <TableCell align='right'>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell {...({ colSpan: columns.length + (canEdit ? 1 : 0) } as object)}>
                  <Typography variant='body2' color='text.secondary'>
                    Nothing here yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                {columns.map((c) => (
                  <TableCell key={c.label} align={c.align}>
                    {c.render(r)}
                  </TableCell>
                ))}
                {canEdit && (
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title='Edit'>
                      <IconButton size='small' onClick={() => setEditing({ row: r, draft: toDraft(r) })}>
                        <Icon icon='mdi:pencil-outline' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete'>
                      <IconButton size='small' color='error' onClick={() => setDeleting(r)}>
                        <Icon icon='mdi:delete-outline' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DialogShell
        open={editing !== null}
        title={`${editing?.row ? 'EDIT' : 'ADD'} ${entityName.toUpperCase()}`}
        onClose={() => setEditing(null)}
        onSubmit={save}
        busy={busy}
      >
        {editing && renderForm(editing.draft, set)}
      </DialogShell>

      <ConfirmationModal
        open={deleting !== null}
        title={`Delete this ${entityName.toLowerCase()}?`}
        message='Months already paid keep their figures; unpaid months will recalculate without it.'
        confirmText='Delete'
        loading={busy}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
      />
    </Stack>
  );
}
